import React, { Fragment, useCallback, useState } from 'react';

import _isFinite from 'lodash/isFinite';
import type { CartItemType } from '@zola-helpers/client/dist/es/@types/cart';
import styled from '@emotion/styled';
import InputFieldV3 from '../../Form/inputV3/InputFieldV3';
import { Input, InputSection } from '../../Form/inputV3/InputFieldV3/InputFieldV3.styles';
import { isDigitalGreetingCard } from '../utils/digitalGreetingCards';

import styles from './cartItemQuantityInput.module.less';
import { RemoveFromCartButton } from './RemoveFromCartButton';

const MIN_QUANTITY = 1;
const MAX_QUANTITY = 100;

const InputFieldV3Styles = styled.div`
  ${InputSection} {
    margin: 0 !important;
  }

  ${Input} {
    height: 44px !important;
  }
`;

export type CartItemQuantityProps = {
  item: CartItemType;
  updateQuantity: (itemId: string, quantity: number) => Promise<void>;
  remove: (itemId: string) => Promise<void>;
};

const resetValueToMaxAvailable = (currentQuantity: number, maxQuantity: number) => {
  return currentQuantity > maxQuantity ? maxQuantity : currentQuantity;
};

export const CartItemQuantityInput = ({ item, remove, updateQuantity }: CartItemQuantityProps) => {
  const [state, setState] = useState({
    persistedQuantity: item.contribution?.quantity,
    quantity: item.contribution?.quantity,
  });
  const { quantity } = state;
  const itemQuantity = item.contribution?.quantity || 0;

  const showQuantityInput = !item.groupGift && itemQuantity > 0;
  const adjustedQuantity = resetValueToMaxAvailable(
    quantity || 0,
    item.contribution?.quantity || 0
  );

  const onUpdateQuantity = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const originalQuantity = Number(event.target.value);
      const updatedQuantity = Number(event.target.value);
      // they entered something other than a number, they may be in the middle of typing something so store it but don't persist
      if (!_isFinite(updatedQuantity)) {
        setState({
          ...state,
          quantity: originalQuantity,
        });
        return;
      }

      if (updatedQuantity < MIN_QUANTITY || updatedQuantity > MAX_QUANTITY) {
        const { persistedQuantity } = state;
        setState({
          ...state,
          quantity: persistedQuantity,
          // inputError: `Please enter a quantity between ${MIN_QUANTITY} and ${MAX_QUANTITY}.`
        });
        return;
      }

      // Update the quantity in the cart
      updateQuantity(item.id as string, updatedQuantity)
        .then(() =>
          setState({
            quantity: updatedQuantity,
            persistedQuantity: updatedQuantity,
            // inputError: null
          })
        )
        .catch(() => {
          const { persistedQuantity } = state;
          setState({
            ...state,
            quantity: persistedQuantity,
            // inputError: error.response.errorMessage
          });
        });
    },
    [item.id, state, updateQuantity]
  );

  const onResetInvalidQuantity = useCallback(() => {
    const { quantity, persistedQuantity } = state;
    const currentQuantity = Number(quantity);
    if (!_isFinite(currentQuantity)) {
      setState({
        ...state,
        quantity: persistedQuantity,
      });
    }
  }, [state]);

  const onRemove = useCallback(() => {
    return remove(item.id as string);
  }, [item.id, remove]);

  return (
    <Fragment>
      {showQuantityInput && (
        <InputFieldV3Styles>
          <InputFieldV3
            id={`cart-qty-${item.id}`}
            containerClassname={styles.cartItemQuantityInput}
            type="number"
            required
            min="1"
            max="100"
            step="1"
            disabled={item.buyGroupGift || !item.isMutable || isDigitalGreetingCard(item)}
            value={`${adjustedQuantity}`}
            onChange={onUpdateQuantity}
            onBlur={onResetInvalidQuantity}
          />
        </InputFieldV3Styles>
      )}
      <RemoveFromCartButton removeFromCart={onRemove} />
    </Fragment>
  );
};
